// src/components/Button/Button.js
import React from "react";

/**
 * Button — polyvalent et élégant (compatible icon={FaSave} ou icon={<FaSave />})
 */
const Button = React.forwardRef(
  (
    {
      text,
      children,
      onClick,
      icon: Icon,
      iconPosition = "left",
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      fullWidth = false,
      className = "",
      hoverLabel = "",
      type = "button",
      ...rest
    },
    ref
  ) => {
    const hasText = !!(text || children);
    const isIconOnly = !!Icon && !hasText;

    const base =
      "inline-flex items-center justify-center font-satoshi select-none transition " +
      "focus-visible:outline-none focus-visible:ring-2 ring-offset-2 " +
      "ring-offset-white dark:ring-offset-slate-900 rounded-xl " +
      "disabled:opacity-60 disabled:pointer-events-none " +
      "hover:-translate-y-0.5 active:translate-y-0 shadow-sm hover:shadow";

    const sizeMap = {
      sm: `h-12 ${isIconOnly ? "w-8" : "px-3 text-sm"} rounded-xl gap-1.5`,
      md: `h-10 ${isIconOnly ? "w-10" : "px-4 text-sm"} rounded-xl gap-2`,
      lg: `h-12 ${isIconOnly ? "w-12" : "px-5 text-base"} rounded-2xl gap-2.5`,
    };

    const variantMap = {
      primary:
        "bg-primary text-white border border-primary hover:bg-primary/90 ring-primary/40",
      outline:
        "bg-transparent text-primary border border-primary hover:bg-primary/10 ring-primary/40",
      subtle:
        "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100 " +
        "border border-slate-200 dark:border-slate-700 hover:bg-slate-200/70 dark:hover:bg-slate-700 ring-slate-400/30",
      ghost:
        "bg-transparent text-slate-700 dark:text-slate-200 border border-transparent " +
        "hover:bg-slate-200/40 dark:hover:bg-slate-700/40 ring-slate-400/30",
      danger:
        "bg-rose-600 text-white border border-rose-700 hover:bg-rose-700 ring-rose-400/40",
      success:
        "bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700 ring-emerald-400/40",
    };

    const spinner = (
      <svg
        className="animate-spin -ml-0.5 mr-2 h-4 w-4"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 0 1 8-8v4A4 4 0 0 0 8 12H4z"
        />
      </svg>
    );

    // 🔧 Rend l’icône qu’elle soit un composant, un élément JSX, ou une string
    const renderIcon = (pos) => {
      if (!Icon) return null;
      const cls = isIconOnly ? "text-lg" : pos === "left" ? "text-lg -ml-0.5" : "text-lg -mr-0.5";

      // icon={<FaSave />} (élément JSX)
      if (React.isValidElement(Icon)) {
        return React.cloneElement(Icon, {
          className: [cls, Icon.props.className].filter(Boolean).join(" "),
        });
      }
      // icon={FaSave} (composant)
      if (typeof Icon === "function") {
        const Comp = Icon;
        return <Comp className={cls} />;
      }
      // icon="✓" (string)
      if (typeof Icon === "string") {
        return <span className={cls}>{Icon}</span>;
      }
      return null;
    };

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        aria-label={hoverLabel}
        title={hoverLabel}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={[
          base,
          sizeMap[size] || sizeMap.md,
          variantMap[variant] || variantMap.primary,
          fullWidth ? "w-full" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        {loading && spinner}

        {Icon && iconPosition === "left" && renderIcon("left")}

        {hasText ? (
          <span className={isIconOnly ? "sr-only" : ""}>{text || children}</span>
        ) : null}

        {Icon && iconPosition === "right" && renderIcon("right")}
      </button>
    );
  }
);

export default Button;
